import { deduplicate } from "@rauricoste/core";
import { ResourcePile } from "./ResourcePile";

export type ResourcesProps = {
  array: ResourcePile[];
};

export type ResourcesLike = ResourcesProps | Resources | Record<string, number>;

export class Resources implements ResourcesProps {
  array: ResourcePile[];

  constructor(props: ResourcesProps) {
    Object.assign(this, props);
    const names = deduplicate(props.array.map((_) => _.name));
    if (names.length !== props.array.length) {
      throw new Error("duplicate resources");
    }
  }

  getResource(name: string): ResourcePile | undefined {
    return this.array.find((_) => _.name === name);
  }

  canPay(resources: ResourcesLike) {
    resources = Resources.parse(resources);
    return resources.array.every((cost) => {
      const current = this.getResource(cost.name);
      return current?.canPay(cost.value);
    });
  }
  pay(resources: ResourcesLike) {
    resources = Resources.parse(resources);
    return resources.array.forEach((cost) => {
      const current = this.getResource(cost.name);
      if (!current) {
        throw new Error("cannot pay");
      }
      return current.pay(cost.value);
    });
  }
  add(resources: ResourcesLike) {
    resources = Resources.parse(resources);
    return resources.array.forEach((cost) => {
      let current = this.getResource(cost.name);
      if (!current) {
        current = new ResourcePile({ ...cost, value: 0 });
        this.array.push(current);
      }
      return current.add(cost.value);
    });
  }
  multiply(factor: number): Resources {
    return new Resources({
      array: this.array.map((resource) => resource.multiply(factor)),
    });
  }
  toString(): string {
    return this.array.map(({ name, value }) => `${name}:${value}`).join(", ");
  }
  static parse(resources: ResourcesLike): Resources {
    if ("array" in resources) {
      return new Resources(resources as ResourcesProps);
    }
    return Resources.fromRecord(resources);
  }
  static fromRecord(values: Record<string, number>): Resources {
    return new Resources({
      array: Object.entries(values).map(
        ([name, value]) =>
          new ResourcePile({
            name,
            value,
          })
      ),
    });
  }
  copy(): Resources {
    return new Resources({
      array: this.array.map((props) => new ResourcePile(props)),
    });
  }
}
